import { CartCouponConstraintTypeEnum, CouponUsageType } from '@src/domains/coupons/types/coupon';
import type { CouponProductScope, ItemWithProductName } from '@src/domains/coupons/types/couponProductScope';

type CouponScopeMatch = {
  ids: string[];
  matchBy: 'appId' | 'productId';
};

const getCouponScopeMatches = (coupon: CouponProductScope): CouponScopeMatch[] => {
  if (coupon.usageType === CouponUsageType.Mixed) {
    const scopes: CouponScopeMatch[] = [];

    if (coupon.appIds?.length) {
      scopes.push({ ids: coupon.appIds, matchBy: 'appId' });
    }

    if (coupon.productIds?.length) {
      scopes.push({ ids: coupon.productIds, matchBy: 'productId' });
    }

    return scopes;
  }

  if (coupon.usageType === CouponUsageType.Product && coupon.productIds?.length) {
    return [{ ids: coupon.productIds, matchBy: 'productId' }];
  }

  return [];
};

const isItemInCouponScopeList = (item: ItemWithProductName, scope: CouponScopeMatch): boolean => {
  const value = scope.matchBy === 'appId' ? item.product.appId : item.product.id;

  return scope.ids.includes(value);
};

const isItemInAnyCouponScope = (item: ItemWithProductName, scopes: CouponScopeMatch[]): boolean =>
  scopes.some((scope) => isItemInCouponScopeList(item, scope));

export const isCouponEligibleItem = (item: Pick<ItemWithProductName, 'productPackageUnitId'>): boolean =>
  !item.productPackageUnitId;

export const getCouponEligibleItems = <T extends ItemWithProductName>(items: T[]): T[] =>
  items.filter(isCouponEligibleItem);

export const hasCouponEligibleItem = (items: Pick<ItemWithProductName, 'productPackageUnitId'>[]): boolean =>
  items.some(isCouponEligibleItem);

export const isItemAffectedByCoupon = (item: ItemWithProductName, coupon: CouponProductScope): boolean => {
  if (coupon.usageType === CouponUsageType.General) {
    return Boolean(coupon.sellerAppId && item.product.appId === coupon.sellerAppId);
  }

  const scopes = getCouponScopeMatches(coupon);

  if (!scopes.length) {
    return false;
  }

  const inList = isItemInAnyCouponScope(item, scopes);

  if (coupon.constraintType === CartCouponConstraintTypeEnum.Exclude) {
    return !inList;
  }

  return inList;
};
